/*
  Light / dark / system switcher.

  The inline script in layout/theme.liquid applies the stored preference
  before first paint. This element owns the UI in the header and keeps
  <html data-theme> in sync with the OS when the preference is "system".
*/
(() => {
  const STORAGE_KEY = 'railway-theme';
  const root = document.documentElement;
  const media = window.matchMedia('(prefers-color-scheme: dark)');

  const readPreference = () => {
    try {
      const value = localStorage.getItem(STORAGE_KEY);
      return value === 'light' || value === 'dark' ? value : 'system';
    } catch (error) {
      return 'system';
    }
  };

  const writePreference = (value) => {
    try {
      if (value === 'system') localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, value);
    } catch (error) {
      /* Storage unavailable (private mode, blocked); the choice lasts for this page only. */
    }
  };

  const apply = (preference) => {
    const resolved = preference === 'system' ? (media.matches ? 'dark' : 'light') : preference;
    root.setAttribute('data-theme', resolved);
    root.setAttribute('data-theme-preference', preference);
    document.querySelectorAll('theme-toggle').forEach((el) => el.sync(preference));
  };

  class ThemeToggle extends HTMLElement {
    constructor() {
      super();
      this.details = this.querySelector('details');
      this.options = Array.from(this.querySelectorAll('[data-theme-choice]'));
      this.options.forEach((button) =>
        button.addEventListener('click', () => {
          const choice = button.dataset.themeChoice;
          writePreference(choice);
          apply(choice);
          this.close();
          this.details.querySelector('summary').focus();
        })
      );
      this.details.addEventListener('keyup', (event) => {
        if (event.code === 'Escape') {
          this.close();
          this.details.querySelector('summary').focus();
        }
      });
    }

    connectedCallback() {
      this.sync(readPreference());
    }

    sync(preference) {
      this.options.forEach((button) => {
        const active = button.dataset.themeChoice === preference;
        button.setAttribute('aria-checked', String(active));
      });
    }

    close() {
      this.details.removeAttribute('open');
      this.details.querySelector('summary').setAttribute('aria-expanded', 'false');
    }
  }

  if (!customElements.get('theme-toggle')) customElements.define('theme-toggle', ThemeToggle);

  // Follow the OS while the visitor has not made an explicit choice.
  const onSystemChange = () => {
    if (readPreference() === 'system') apply('system');
  };
  if (typeof media.addEventListener === 'function') media.addEventListener('change', onSystemChange);
  else if (typeof media.addListener === 'function') media.addListener(onSystemChange);

  apply(readPreference());
})();
